import React from 'react';
import type { RouteProp } from '@react-navigation/native';
import { useRoute } from '@react-navigation/native';
import {
  ModalFloatingCloseBtn,
  useBreakpointHelpers,
} from '@sweetgreen/garnish';

import {
  AddressForm,
  LoadingAnimation,
  NotFoundView,
  PageWrapper,
  TransparentModal,
  useAddressHandlers,
} from '@order/components';
import { useCustomer } from '@order/Customer';
import type { Address } from '@order/graphql';
import { useLocalizationContext } from '@order/Localization';

import { useNavigateBack } from '../../navigation';
import type {
  AddressEditScreenProps,
  ModalStackParamList,
} from '../../navigation/AppNavigation.props';

export const AddressEditScreen = ({ route }: AddressEditScreenProps) => {
  const { t } = useLocalizationContext();
  const editingAddressId = route.params?.addressId;

  const handleGoBack = useNavigateBack();

  const { isMobile } = useBreakpointHelpers();

  const {
    params: { addressId },
  } = useRoute<RouteProp<ModalStackParamList, 'AddressEdit'>>();
  const {
    customer: { addresses },
    isCustomerLoaded,
  } = useCustomer();

  const {
    isUpdatingAddress,
    isDeletingAddress,
    handleOnSave,
    handleOnEdit,
    handleOnDelete,
  } = useAddressHandlers({
    editingAddressId: addressId,
    checkExistingBag: true,
    onClose: handleGoBack,
  });

  const address = addresses?.find((item: Address) => item.id === addressId);

  if (!isCustomerLoaded) return <LoadingAnimation />;

  if (isCustomerLoaded && !address) return <NotFoundView />;

  return (
    <TransparentModal size="small" onClose={handleGoBack}>
      {isMobile ? null : <ModalFloatingCloseBtn onPress={handleGoBack} />}
      <PageWrapper
        withHeaderTitle
        withoutFooter
        testID="account.edit-address.root.container"
      >
        <AddressForm
          address={address}
          visible={true}
          editing={true}
          loading={isUpdatingAddress}
          deleting={isDeletingAddress}
          noHeader={false}
          disableAddress={false}
          editingAddressId={editingAddressId}
          submitCta={t('account.addresses.save')}
          onSave={handleOnSave}
          onEdit={handleOnEdit}
          onDelete={handleOnDelete}
        />
      </PageWrapper>
    </TransparentModal>
  );
};
